/* eslint-disable no-shadow */
import { call, takeEvery } from "redux-saga/effects";
import noop from "lodash.noop";
import { getId, injectApiWithContextFactory, injectBoundActionCreatorsFactory } from "Utils/sagaUtils";
import { injectBoundGQLClient } from "Utils/injectBoundGQLClient";
import {
  loadInstanceContext, loadUserContext, updateInstanceAboutCompany, updateUserContextAboutYou 
} from "./preferences.api";
import { loadChainInstance } from "../manager/userPrompt/userPrompt.api";
import {
  AI_PREFERENCES_LOAD_REQUESTED,
  AI_PREFERENCES_SAVE_ABOUT_COMPANY_REQUESTED,
  AI_PREFERENCES_SAVE_ABOUT_YOU_REQUESTED,
  appMngmtId,
  loadFail,
  loadOptions,
  loadSuccess,
  saveAboutCompanyFail,
  saveAboutCompanyRequested,
  saveAboutCompanySuccess,
  saveAboutYouFail,
  saveAboutYouRequested,
  saveAboutYouSuccess,
} from "./preferences.duck";

export const withBoundActions = injectBoundActionCreatorsFactory(
  {
    loadFail,
    loadOptions,
    loadSuccess,
    saveAboutCompanyFail,
    saveAboutCompanyRequested,
    saveAboutCompanySuccess,
    saveAboutYouFail,
    saveAboutYouRequested,
    saveAboutYouSuccess,
  },
  () => appMngmtId
);

export const withApiContext = injectApiWithContextFactory(
  {
    loadChainInstance,
    loadInstanceContext,
    loadUserContext,
    updateInstanceAboutCompany,
    updateUserContextAboutYou,
  },
  injectBoundGQLClient
);

function* loadAIPreferences({
  api: { loadUserContext = noop, loadInstanceContext = noop, loadChainInstance = noop },
  actions: { loadSuccess = noop, loadFail = noop, loadOptions = noop },
  resolve = noop,
  reject = noop
}) {
  try {
    // Load user context, instance context, and chain instance asynchronously
    const userContext = yield loadUserContext();
    const instanceContext = yield loadInstanceContext();
    const chainInstance = yield loadChainInstance();

    // Extract relevant information from the loaded contexts and instances
    yield loadSuccess({
      aboutYou: {
        position: userContext?.position || null,
        level: userContext?.level || null
      },
      aboutCompany: {
        name: instanceContext?.name || null,
        website: instanceContext?.website || null,
        industry: instanceContext?.industry || null,
        description: instanceContext?.description || null
      }
    });

    // Extract industry and level information from instance and chain
    const companyIndustry = instanceContext?.industry;
    const industries = chainInstance?.chain?.map(c => c?.criteria?.company?.industry || []) || [];
    const userLevel = userContext?.level || null;
    const levels = chainInstance?.chain?.map(c => c?.criteria?.user?.level || []) || [];

    // Remove duplicate and undefined values from levels and industries
    const uniqueLevels = Array.from(new Set(levels.flat().filter(s => s !== null && s !== undefined)));
    const uniqueIndustries = Array.from(new Set(industries.flat().filter(s => s !== null && s !== undefined)));

    // Map unique levels and industries to options format
    const levelOptions = uniqueLevels.map(p => ({ value: p, label: p }));
    const industryOptions = uniqueIndustries.map(p => ({ value: p, label: p }));

    // Create default options with a null option at the beginning
    const defaultOptLevel = [ { label: null, value: "" }, ...levelOptions ];
    const defaultOptIndustries = [ { label: null, value: "" }, ...industryOptions ];

    // Add user's level and company's industry to default options if not already present
    if (defaultOptLevel.findIndex(o => o.value === userLevel) === -1 &&![
      null, undefined, "" 
    ].includes(userLevel?.trim())) {
      defaultOptLevel.push({ label: userLevel, value: userLevel, disabled: true });
    }

    if (defaultOptIndustries.findIndex(o => o.value === companyIndustry) === -1 && ![
      null, undefined, "" 
    ]
      .includes(companyIndustry?.trim())) {
      defaultOptIndustries.push({ label: companyIndustry, value: companyIndustry, disabled: true });
    }

    // Sort default options by value
    const sortedLevels = defaultOptLevel.sort((a, b) => (a?.value > b?.value) ? 1 : -1);
    const sortedIndustries = defaultOptIndustries.sort((a, b) => (a?.value > b?.value) ? 1 : -1);

    // Load sorted options and call the resolve function
    yield loadOptions({ levels: sortedLevels, industries: sortedIndustries });
    yield call(resolve);
  } catch (error) {
    // If there's an error, call the reject function and load the error
    yield call(reject);
    yield loadFail(error);
  }

}

function* saveAboutYou({
  data,
  api: { updateUserContextAboutYou = noop },
  actions: { saveAboutYouFail = noop, saveAboutYouSuccess = noop },
  resolve = noop,
  reject = noop
}) {
  try {
    const aboutYou = {
      position: data?.position || null,
      level: data?.level || null
    };
    const response = yield updateUserContextAboutYou({ formValues: aboutYou });
    yield saveAboutYouSuccess({
      position: response?.position || null,
      level: response?.level || null
    });
    yield call(resolve);
  } catch (error) {
    yield saveAboutYouFail(error);
    yield call(reject);
  }
}

function* saveAboutCompany({
  data,
  api: { updateInstanceAboutCompany = noop },
  actions: { saveAboutCompanyFail = noop, saveAboutCompanySuccess = noop },
  resolve = noop,
  reject = noop,
  mdContext = {},
}) {
  try {
    const response = yield updateInstanceAboutCompany({ id: mdContext.instance, context: data });
    yield saveAboutCompanySuccess({
      name: response?.name || null,
      website: response?.website || null,
      industry: response?.industry || null,
      description: response?.description || null,
    });
    yield call(resolve);
  } catch (error) {
    yield saveAboutCompanyFail(error);
    yield call(reject);
  }
}

export default () =>
  function* rootSaga() {
    yield takeEvery(AI_PREFERENCES_LOAD_REQUESTED, withApiContext(withBoundActions(loadAIPreferences)));
    yield takeEvery(AI_PREFERENCES_SAVE_ABOUT_YOU_REQUESTED, withApiContext(withBoundActions(saveAboutYou)));
    yield takeEvery(AI_PREFERENCES_SAVE_ABOUT_COMPANY_REQUESTED, withApiContext(withBoundActions(saveAboutCompany)));
  };
